import moment from 'moment';

/**
 * "Past" and "live" are date-derived, so both the listing and the map panel
 * decide them here rather than each rolling its own predicate. The backend
 * already honours `hidePastEvents` / `hideLiveEvents`; re-applying them on the
 * client keeps cached, appended or map-side results from leaking an event the
 * active filter excludes.
 */
export const isActiveOrFuture = (event, now = moment()) => {
  const date = event?.date;
  if (!date) return true;
  if (typeof date === 'string') {
    const m = moment(date);
    return m.isValid() ? m.isSameOrAfter(now, 'day') : true;
  }
  if (typeof date === 'object') {
    const { type, endDate, startDate, days } = date;
    if (type === 'fixed') {
      const ref = endDate || startDate;
      return ref ? moment(ref).isSameOrAfter(now, 'day') : true;
    }
    if (type === 'segmented' && Array.isArray(days)) {
      return days.some(({ date: d }) =>
        moment(d, 'YYYY-MM-DD').isSameOrAfter(now, 'day')
      );
    }
  }
  return true;
};

// "Live" = today falls within the event's date range. Hiding live events
// leaves both finished events (already ended) and upcoming events (haven't
// started yet) visible.
export const isNotLive = (event, now = moment()) => {
  const date = event?.date;
  if (!date) return true;
  if (typeof date === 'string') {
    const m = moment(date);
    if (!m.isValid()) return true;
    return !m.isSame(now, 'day');
  }
  if (typeof date === 'object') {
    const { type, startDate, endDate, days } = date;
    if (type === 'fixed') {
      const start = startDate || endDate;
      const end = endDate || startDate;
      if (!start || !end) return true;
      const startM = moment(start);
      const endM = moment(end);
      return endM.isBefore(now, 'day') || startM.isAfter(now, 'day');
    }
    if (type === 'segmented' && Array.isArray(days) && days.length > 0) {
      const valid = days
        .map(({ date: d }) => moment(d, 'YYYY-MM-DD'))
        .filter((m) => m.isValid());
      if (valid.length === 0) return true;
      const minDay = moment.min(valid);
      const maxDay = moment.max(valid);
      return maxDay.isBefore(now, 'day') || minDay.isAfter(now, 'day');
    }
  }
  return true;
};

const applyEventTimeFilters = (
  list,
  { hidePastEvents = false, hideLiveEvents = false } = {}
) => {
  const events = list || [];
  if (!hidePastEvents && !hideLiveEvents) return events;

  const now = moment();
  let filtered = events;
  if (hidePastEvents)
    filtered = filtered.filter((e) => isActiveOrFuture(e, now));
  if (hideLiveEvents) filtered = filtered.filter((e) => isNotLive(e, now));
  return filtered;
};

export default applyEventTimeFilters;
